import React, { useEffect, useRef, useState } from 'react';
import { styles } from './styles';

type Sentence = {
  text: string;
  start: number;
  end: number;
};

type TeleprompterProps = {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  sentences?: Sentence[];
};

export const Teleprompter: React.FC<TeleprompterProps> = ({ audioRef, sentences }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [userScrolled, setUserScrolled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setUserScrolled(true);
      clearTimeout((handleScroll as any).timeout);
      (handleScroll as any).timeout = setTimeout(() => {
        setUserScrolled(false);
      }, 3000); // 3 seconds without scroll = allow auto-scroll again
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Update current time from audio
  useEffect(() => {
    const interval = setInterval(() => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [audioRef]);

  // Auto-scroll to active sentence
  useEffect(() => {
    const container = containerRef.current;
    const activeEl = document.querySelector('.sentence.active') as HTMLElement;
    if (!container || !activeEl) return;

    const containerTop = container.scrollTop;
    const containerHeight = container.clientHeight;

    const activeTop = activeEl.offsetTop;
    const activeHeight = activeEl.offsetHeight;

    const isVisible = activeTop >= containerTop && activeTop + activeHeight <= containerTop + containerHeight;

    if (!userScrolled && !isVisible) {
      container.scrollTo({
        top: activeTop - containerHeight / 2 + activeHeight / 2,
        behavior: 'smooth',
      });
    }
  }, [currentTime, userScrolled]);

  return (
    <div
      id="TeleprompterContainer"
      style={{
        background: '#003461',
        color: 'white',
        fontSize: '1.2rem',
        fontFamily: 'sans-serif',
        flexGrow: 1,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          overflowY: 'auto',
          bottom: 0,
          padding: '1rem',
          lineHeight: '2',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          ref={containerRef}
          style={{
            overflowY: 'auto',
            padding: '1rem',
            lineHeight: '2',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sentences?.map((sentence, i) => {
            const isActive = currentTime >= sentence.start && currentTime <= sentence.end;
            const hasPassed = currentTime >= sentence.start;

            return (
              <div
                key={i}
                className={`sentence ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (audioRef.current) {
                    audioRef.current.currentTime = Math.max(0, sentence.start - 0.3);
                    console.log('sentence start', sentence.start, sentence);
                    // play audio if not already playing
                    audioRef.current.play();
                  }
                }}
                style={{
                  ...styles.sentence,
                  textDecoration: isActive ? 'underline' : 'auto',
                  opacity: hasPassed ? 1 : 0.5,
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLDivElement).style.textDecoration = 'auto';
                  }
                }}
              >
                {sentence.text}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
